import { db } from '../firebase-config';
import { getAuth } from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  getDocs,
} from 'firebase/firestore';
import HoursAccount, { HoursAdjustment } from '../models/HoursAccountModel';

class HoursAccountService {

  // ---------------------------------------------------------------------------
  // Reference helper
  // ---------------------------------------------------------------------------

  /**
   * Returns the Firestore document reference for a specific hours account.
   *
   * @param {string} companyId
   * @param {string} staffId
   * @param {string|number} year
   * @returns {DocumentReference}
   */
  getAccountRef(companyId, staffId, year) {
    return doc(
      db,
      'companies', companyId,
      'staff', staffId,
      'hoursAccounts', year.toString()
    );
  }

  // ---------------------------------------------------------------------------
  // Read
  // ---------------------------------------------------------------------------

  /**
   * Fetch a single hours account. Returns null if the document does not exist.
   *
   * @param {string} companyId
   * @param {string} staffId
   * @param {string|number} year
   * @returns {Promise<HoursAccount|null>}
   */
  async getAccount(companyId, staffId, year) {
    try {
      const accountRef = this.getAccountRef(companyId, staffId, year);
      const accountDoc = await getDoc(accountRef);

      if (accountDoc.exists()) {
        return HoursAccount.fromFirestore(accountDoc.data());
      }

      return null;
    } catch (err) {
      console.error('HoursAccountService.getAccount error:', err);
      throw err;
    }
  }

  /**
   * Fetch all hours accounts for a staff member, sorted descending by year.
   *
   * @param {string} companyId
   * @param {string} staffId
   * @returns {Promise<HoursAccount[]>}
   */
  async getAllAccounts(companyId, staffId) {
    try {
      const accountsRef = collection(
        db,
        'companies', companyId,
        'staff', staffId,
        'hoursAccounts'
      );
      const snapshot = await getDocs(accountsRef);

      const accounts = snapshot.docs.map(d => HoursAccount.fromFirestore(d.data()));

      accounts.sort((a, b) => parseInt(b.year, 10) - parseInt(a.year, 10));

      return accounts;
    } catch (err) {
      console.error('HoursAccountService.getAllAccounts error:', err);
      throw err;
    }
  }

  /**
   * Check whether a hours account document exists for a given year.
   *
   * @param {string} companyId
   * @param {string} staffId
   * @param {string|number} year
   * @returns {Promise<boolean>}
   */
  async accountExists(companyId, staffId, year) {
    try {
      const accountRef = this.getAccountRef(companyId, staffId, year);
      const accountDoc = await getDoc(accountRef);
      return accountDoc.exists();
    } catch (err) {
      console.error('HoursAccountService.accountExists error:', err);
      throw err;
    }
  }

  // ---------------------------------------------------------------------------
  // Create
  // ---------------------------------------------------------------------------

  /**
   * Create a new hours account for the given year. Throws if one already exists.
   *
   * @param {string} companyId
   * @param {string} staffId
   * @param {string|number} year
   * @param {Object} data - Optional overrides. Supports { openingBalance }.
   * @returns {Promise<HoursAccount>}
   */
  async createAccount(companyId, staffId, year, data = {}) {
    try {
      const accountRef = this.getAccountRef(companyId, staffId, year);
      const accountDoc = await getDoc(accountRef);

      if (accountDoc.exists()) {
        throw new Error(`Hours account for year ${year} already exists`);
      }

      const account = HoursAccount.createDefault(year, data.openingBalance || 0);
      await setDoc(accountRef, account.toFirestore());

      return account;
    } catch (err) {
      console.error('HoursAccountService.createAccount error:', err);
      throw err;
    }
  }

  /**
   * Return the existing hours account for the given year, or silently create
   * and return a default one if it does not yet exist. This is the primary
   * load method called by HoursAccountCard on mount — it never throws a
   * "does not exist" error.
   *
   * @param {string} companyId
   * @param {string} staffId
   * @param {string|number} year
   * @returns {Promise<HoursAccount>}
   */
  async ensureAccount(companyId, staffId, year) {
    try {
      const accountRef = this.getAccountRef(companyId, staffId, year);
      const accountDoc = await getDoc(accountRef);

      if (accountDoc.exists()) {
        return HoursAccount.fromFirestore(accountDoc.data());
      }

      const account = HoursAccount.createDefault(year, 0);
      await setDoc(accountRef, account.toFirestore());

      return account;
    } catch (err) {
      console.error('HoursAccountService.ensureAccount error:', err);
      throw err;
    }
  }

  // ---------------------------------------------------------------------------
  // Write — opening balance
  // ---------------------------------------------------------------------------

  /**
   * Update the opening balance and recalculate the YTD balance.
   * The web layer only writes openingBalance and the derived ytdBalance;
   * monthlyBalances and lastCalculatedAt remain Cloud Function territory.
   *
   * @param {string} companyId
   * @param {string} staffId
   * @param {string|number} year
   * @param {number} hours - Signed decimal hours (positive = overtime, negative = undertime)
   * @returns {Promise<HoursAccount>}
   */
  async setOpeningBalance(companyId, staffId, year, hours) {
    if (!companyId || !staffId || !year) {
      throw new Error('companyId, staffId, and year are required');
    }

    try {
      const accountRef = this.getAccountRef(companyId, staffId, year);
      const accountDoc = await getDoc(accountRef);

      if (!accountDoc.exists()) {
        throw new Error(`Hours account for year ${year} does not exist`);
      }

      const account = HoursAccount.fromFirestore(accountDoc.data());
      account.openingBalance = parseFloat(hours);
      account.recalculate();

      await updateDoc(accountRef, {
        openingBalance: account.openingBalance,
        ytdBalance:     account.ytdBalance,
        updatedAt:      new Date(),
      });

      return account;
    } catch (err) {
      console.error('HoursAccountService.setOpeningBalance error:', err);
      throw err;
    }
  }

  // ---------------------------------------------------------------------------
  // Write — adjustments
  // ---------------------------------------------------------------------------

  /**
   * Append a manual adjustment to the account and recalculate the YTD balance.
   *
   * @param {string} companyId
   * @param {string} staffId
   * @param {string|number} year
   * @param {Object} adjustment - Plain object matching HoursAdjustment constructor params
   * @returns {Promise<HoursAccount>}
   */
  async addAdjustment(companyId, staffId, year, adjustment) {
    try {
      const accountRef = this.getAccountRef(companyId, staffId, year);
      const accountDoc = await getDoc(accountRef);

      if (!accountDoc.exists()) {
        throw new Error(`Hours account for year ${year} does not exist`);
      }

      const account = HoursAccount.fromFirestore(accountDoc.data());
      account.adjustments.push(new HoursAdjustment(adjustment));
      account.recalculate();

      await updateDoc(accountRef, {
        adjustments: account.adjustments.map(a => a.toFirestore()),
        ytdBalance:  account.ytdBalance,
        updatedAt:   new Date(),
      });

      return account;
    } catch (err) {
      console.error('HoursAccountService.addAdjustment error:', err);
      throw err;
    }
  }

  /**
   * Remove an adjustment by index and recalculate the YTD balance.
   *
   * @param {string} companyId
   * @param {string} staffId
   * @param {string|number} year
   * @param {number} adjustmentIndex - Zero-based index into the adjustments array
   * @returns {Promise<HoursAccount>}
   */
  async deleteAdjustment(companyId, staffId, year, adjustmentIndex) {
    if (!companyId || !staffId || !year) {
      throw new Error('companyId, staffId, and year are required');
    }

    if (typeof adjustmentIndex !== 'number' || adjustmentIndex < 0) {
      throw new Error('adjustmentIndex must be a non-negative number');
    }

    try {
      const accountRef = this.getAccountRef(companyId, staffId, year);
      const accountDoc = await getDoc(accountRef);

      if (!accountDoc.exists()) {
        throw new Error(`Hours account for year ${year} does not exist`);
      }

      const account = HoursAccount.fromFirestore(accountDoc.data());

      if (adjustmentIndex >= account.adjustments.length) {
        throw new Error(
          `adjustmentIndex ${adjustmentIndex} is out of range (length: ${account.adjustments.length})`
        );
      }

      account.adjustments.splice(adjustmentIndex, 1);
      account.recalculate();

      await updateDoc(accountRef, {
        adjustments: account.adjustments.map(a => a.toFirestore()),
        ytdBalance:  account.ytdBalance,
        updatedAt:   new Date(),
      });

      return account;
    } catch (err) {
      console.error('HoursAccountService.deleteAdjustment error:', err);
      throw err;
    }
  }

  // ---------------------------------------------------------------------------
  // Write — recalculation (Cloud Function, admin-only)
  // ---------------------------------------------------------------------------

  /**
   * Recalculate a single closed month via the `hoursAccountNightlyRecalc`
   * Cloud Function's guarded historical-recalc branch. Unlike every other
   * write method in this class, the actual mutation happens server-side
   * (Admin SDK) — this is a plain authenticated HTTP call, not a direct
   * Firestore write.
   *
   * - `preview: true`  — dry run. Returns the raw Cloud Function response
   *   `{ companyId, staffId, year, month, key, preview: true, current, computed }`
   *   and writes nothing. `current` is the presently stored monthlyBalances[key]
   *   entry (or null), `computed` is `{ worked, target, balance }` as it would
   *   become.
   * - `preview: false` (default) — performs the write server-side, then
   *   re-fetches the account so this method returns a `Promise<HoursAccount>`,
   *   matching the contract of setOpeningBalance/addAdjustment/deleteAdjustment
   *   above.
   *
   * @param {string} companyId
   * @param {string} staffId
   * @param {string|number} year
   * @param {string|number} month - 1-12
   * @param {{preview?: boolean}} options
   * @returns {Promise<HoursAccount|Object>}
   */
  async recalculateMonth(companyId, staffId, year, month, { preview = false } = {}) {
    if (!companyId || !staffId || !year || !month) {
      throw new Error('companyId, staffId, year, and month are required');
    }

    try {
      const currentUser = getAuth().currentUser;
      if (!currentUser) {
        throw new Error('Not authenticated');
      }

      const url = process.env.REACT_APP_HOURS_RECALC_URL;
      if (!url) {
        throw new Error('REACT_APP_HOURS_RECALC_URL is not configured');
      }

      const token = await currentUser.getIdToken();

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type':  'application/json',
          Authorization:   `Bearer ${token}`,
        },
        body: JSON.stringify({ companyId, staffId, year, month, preview }),
      });

      if (!res.ok) {
        const message = await res.text();
        throw new Error(message || `Recalculation failed (${res.status})`);
      }

      const data = await res.json();

      if (preview) {
        return data;
      }

      // The Cloud Function already wrote monthlyBalances + ytdBalance via the
      // Admin SDK. Re-fetch so callers get a full HoursAccount, same as every
      // other write method here.
      return this.getAccount(companyId, staffId, year);
    } catch (err) {
      console.error('HoursAccountService.recalculateMonth error:', err);
      throw err;
    }
  }

  // ---------------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------------

  /**
   * Permanently delete the hours account document for the given year.
   *
   * @param {string} companyId
   * @param {string} staffId
   * @param {string|number} year
   * @returns {Promise<void>}
   */
  async deleteHoursAccount(companyId, staffId, year) {
    if (!companyId || !staffId || !year) {
      throw new Error('companyId, staffId, and year are required');
    }

    try {
      const accountRef = this.getAccountRef(companyId, staffId, year);
      const accountDoc = await getDoc(accountRef);

      if (!accountDoc.exists()) {
        throw new Error(`No hours account found for year ${year}`);
      }

      await deleteDoc(accountRef);

      console.log(
        `Hours account for staff ${staffId} year ${year} deleted successfully`
      );
    } catch (err) {
      console.error('HoursAccountService.deleteHoursAccount error:', err);
      throw err;
    }
  }
}

export default new HoursAccountService();